import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import CButton from '@/components/shared/CButton';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import CInput from '@/components/shared/CInput';
import CSelect, { valueReducer } from '@/components/shared/CSelect';
import CrosswordStyleList from './components/list/CrosswordStyleList.vue';
import CrosswordStyleSelectModal from './components/create/CrosswordStyleSelectModal.vue';
import CrosswordCustomStyleModal from './components/create/CrosswordCustomStyleModal.vue';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import { CROSSWORD_LANGUAGES, DEFAULT_LANGUAGE } from './core/presets';
import type { CrosswordSettings, CrosswordStyle, CrosswordStyleDraft } from './types';


export default /*#__PURE__*/_defineComponent({
  __name: 'CrosswordProperties',
  props: {
    canvas: null,
    imageElement: null,
    editMode: { type: Boolean }
  },
  emits: ["run-test"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'run-test', element: ImagePlaceholderElement): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
};





const LANGUAGE_OPTIONS = Object.entries(CROSSWORD_LANGUAGES).map(([value, label]) => ({ value, label }));

const MIN_TILE_SPACING = 0;
const MAX_TILE_SPACING = 64;
const DEFAULT_TILE_SPACING = 5;

const STYLE_MODES: CButtonGroupItem<boolean>[] = [
    { label: 'Use as Style Options', value: false },
    { label: 'Alternate Between Styles', value: true }
];

const settings = ref<CrosswordSettings>({ styles: [] });
const styles = computed(() => settings.value.styles);
const selectModalOpen = ref(false);
const customModalOpen = ref(false);
const styleToEdit = ref<CrosswordStyle | null>(null);

const disabled = computed(() => !!props.imageElement?.locked);
const addedPresetIds = computed(() =>
    styles.value.map((style) => style.presetId).filter((presetId): presetId is string => !!presetId)
);
const nextCustomStyleName = computed(() => `Custom Style ${styles.value.filter((s) => s.custom).length + 1}`);

const saveSettings = (patch: Partial<CrosswordSettings>) => {
    const element = props.imageElement;
    if (!element || disabled.value) return;

    // Everything not in the panel comes from the generator defaults until we expose it in the UI
    settings.value = { ...settings.value, ...patch };
    element.set('crosswordSettings', { ...settings.value });

    if (props.editMode) element.setEditedProp('crosswordSettings');
    props.canvas.requestRenderAll();
};

// Ids are never reused, an option set may already point at a deleted one
const nextStyleId = () => styles.value.reduce((max, style) => Math.max(max, style.id), 0) + 1;

const addStyle = (style: CrosswordStyleDraft) => {
    const added = { ...style, id: nextStyleId() };

    saveSettings({ styles: [...styles.value, added] });
    selectTestStyle(added);
};

const removeStyle = (style: CrosswordStyle) => {
    saveSettings({ styles: styles.value.filter((item) => item.id !== style.id) });
};

// The first style is the one rendered when styles are shopper options
const reorderStyles = (newStyles: CrosswordStyle[]) => {
    saveSettings({ styles: newStyles });
};

const language = computed({
    get: () => settings.value.language || DEFAULT_LANGUAGE,
    set: (value: string) => saveSettings({ language: value })
});

// Letter values were always rendered before the toggle existed, so an absent setting means enabled
const scrabbleMode = computed({
    get: () => settings.value.showLetterValues ?? true,
    set: (value: boolean) => {
        saveSettings({ showLetterValues: value });
        runTest();
    }
});

// The input always shows a value, an unset setting falls back to the generator default
const tileSpacing = ref<number>(DEFAULT_TILE_SPACING);

const tileSpacingValue = computed(() => {
    const value = Number(tileSpacing.value);

    return Number.isFinite(value) && value >= MIN_TILE_SPACING && value <= MAX_TILE_SPACING ? value : undefined;
});

// An out of range value keeps the last saved setting until it is corrected
const onTileSpacingChanged = () => {
    const value = tileSpacingValue.value;
    if (value === undefined || settings.value.tileSpacing === value) return;

    saveSettings({ tileSpacing: value });
    runTest();
};

// Testing preference: mirrored onto the element for the test generation, never saved with the template.
// It lives in a local ref because fabric objects are not reactive, a computed over the element would not update.
const testStyleId = ref<number | undefined>();

// Asks the design studio for a new test render with the current settings
const runTest = () => {
    if (props.imageElement) emit('run-test', props.imageElement);
};

const selectTestStyle = (style: CrosswordStyle) => {
    if (testStyleId.value === style.id) return;

    testStyleId.value = style.id;
    props.imageElement?.set('crosswordTestStyleId', style.id);
    runTest();
};

const alternateStyles = computed({
    get: () => !!settings.value.alternateStyles,
    set: (value: boolean) => {
        saveSettings({ alternateStyles: value });
        // Alternating renders every style, so the result changes with the mode
        runTest();
    }
});

const openSelectModal = () => {
    selectModalOpen.value = true;
};

const onPresetSelected = (preset: CrosswordStyleDraft) => {
    addStyle(preset);
    selectModalOpen.value = false;
};

const onCreateCustom = () => {
    selectModalOpen.value = false;
    customModalOpen.value = true;
};

const editStyle = (style: CrosswordStyle) => {
    styleToEdit.value = style;
    customModalOpen.value = true;
};

const onCustomStyleSaved = (draft: CrosswordStyleDraft) => {
    const editing = styleToEdit.value;

    if (editing) {
        // An edited preset stops being one, so its catalog thumbnail is dropped along with the preset id
        const updated = { ...draft, id: editing.id };
        saveSettings({ styles: styles.value.map((item) => (item.id === editing.id ? updated : item)) });

        if (editing.id === testStyleId.value) runTest();
    } else {
        addStyle(draft);
    }

    customModalOpen.value = false;
    styleToEdit.value = null;
};

const onCustomStyleBack = () => {
    customModalOpen.value = false;

    // Editing has nowhere to go back to, only creating comes from the picker
    if (styleToEdit.value) {
        styleToEdit.value = null;
        return;
    }

    selectModalOpen.value = true;
};

// Falls back to the first style when nothing is picked yet or the picked one is gone
watch(
    [styles, testStyleId],
    ([currentStyles, currentId], previous) => {
        if (currentStyles.some((style) => style.id === currentId)) return;

        const fallback = currentStyles[0]?.id;
        testStyleId.value = fallback;
        props.imageElement?.set('crosswordTestStyleId', fallback);

        // Only re-render when the style being tested disappeared, not while loading the element
        if (previous && fallback !== undefined) runTest();
    },
    { immediate: true }
);

watch(
    () => props.imageElement,
    (element) => {
        testStyleId.value = element?.crosswordTestStyleId;
        settings.value = element?.crosswordSettings
            ? { ...element.crosswordSettings, styles: [...(element.crosswordSettings.styles || [])] }
            : { styles: [] };
        tileSpacing.value = settings.value.tileSpacing ?? DEFAULT_TILE_SPACING;
    },
    { immediate: true }
);

return { __sfc: true,props, emit, LANGUAGE_OPTIONS, MIN_TILE_SPACING, MAX_TILE_SPACING, DEFAULT_TILE_SPACING, STYLE_MODES, settings, styles, selectModalOpen, customModalOpen, styleToEdit, disabled, addedPresetIds, nextCustomStyleName, saveSettings, nextStyleId, addStyle, removeStyle, reorderStyles, language, scrabbleMode, tileSpacing, tileSpacingValue, onTileSpacingChanged, testStyleId, runTest, selectTestStyle, alternateStyles, openSelectModal, onPresetSelected, onCreateCustom, editStyle, onCustomStyleSaved, onCustomStyleBack, CButton, CButtonGroup, CCheckbox, CInput, CSelect, valueReducer, CrosswordStyleList, CrosswordStyleSelectModal, CrosswordCustomStyleModal }
}

})